import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid
} from '@mui/material'
import {
  Add,
  People,
  TrendingUp,
  AttachMoney
} from '@mui/icons-material'

import useAuthStore from '../../store/authStore'
import RoleGuard from '../../components/Auth/RoleGuard'
import ClientsTable from '../../components/Clients/ClientsTable'
import ClientForm from '../../components/Clients/ClientForm'
import { useClients } from '../../hooks/useClients'
import { formatCurrency } from '../../utils/formatters'

const ClientsPage = () => {
  const navigate = useNavigate()
  const { isAdmin } = useAuthStore()
  const [showForm, setShowForm] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)

  // Get summary data
  const { data: summaryData } = useClients({ page: 1, limit: 1 })

  const handleCreateClient = () => {
    setSelectedClient(null)
    setShowForm(true)
  }

  const handleEditClient = (client) => {
    setSelectedClient(client)
    setShowForm(true)
  }

  const handleViewClient = (client) => {
    navigate(`/clients/${client.id}`)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setSelectedClient(null)
  }

  // Calculate summary stats from the data
  const totalClients = summaryData?.pagination?.total || 0
  const activeClients = summaryData?.clients?.filter(c => c.active)?.length || 0

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Clientes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestiona la información de tus clientes y su estado de cuenta
          </Typography>
        </Box>
        
        <RoleGuard requireAdmin fallback={null}>
          <Button
            variant="contained"
            startIcon={<Add />}
            size="large"
            onClick={handleCreateClient}
            sx={{ borderRadius: 2 }}
          >
            Nuevo Cliente
          </Button>
        </RoleGuard>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {totalClients}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Clientes
                  </Typography>
                </Box>
                <People sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {activeClients}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Clientes Activos
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {summaryData?.clients?.filter(c => parseFloat(c.totalDebt || 0) > 0)?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Con Deuda
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: 'warning.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    {formatCurrency(
                      summaryData?.clients?.reduce((sum, c) => sum + parseFloat(c.totalDebt || 0), 0) || 0
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Deuda Total
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: 'error.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Clients Table */}
      <ClientsTable 
        onEdit={handleEditClient}
        onView={handleViewClient}
      />

      {/* Client Form Dialog */}
      <ClientForm
        open={showForm}
        onClose={handleCloseForm}
        client={selectedClient}
      />
    </Box>
  )
}

export default ClientsPage
